import { draftLines, draftSubtotal, sharesFor } from '@/lib/recurring-draft'
import type { GroupDraft } from '@/lib/recurring-draft'
import type { Beneficiary } from '@/lib/recurring-types'
import { allocateShares, sharePercent, toCents } from '@/lib/shares'

export interface LivePart {
  description: string
  percentage: number
  cents: number
}

export interface LiveShareLine {
  beneficiary: string
  label: string
  cents: number
  parts: LivePart[]
}

export interface LiveDistribution {
  /** false betekent: nergens aandelen, dus het verdeel-script van de klant beslist straks. */
  hasDistribution: boolean
  /** false zolang een verdeling niet sluit of een regel ongedekt is; dan is er geen uitkomst. */
  complete: boolean
  subtotalCents: number
  lines: LiveShareLine[]
}

/**
 * Wat elke ontvanger krijgt als deze reeks nu een concept zou opleveren, gerekend
 * op de niet-opgeslagen draft. Spiegelt App\Recurring\RecurringDistributionResolver
 * plus App\Distribution\LineShareDistributionProvider: per regel de eigen aandelen
 * of anders die van de groep, hele centen via grootste-rest, en ontvangers die op
 * nul uitkomen vallen weg, precies zoals de backend ze weglaat.
 */
export function liveDistribution(groups: GroupDraft[], beneficiaries: Beneficiary[]): LiveDistribution {
  const subtotalCents = draftSubtotal(groups)
  const lines = draftLines(groups)
  const labelOf = (id: string): string => beneficiaries.find((b) => b.id === id)?.label ?? '?'

  const covered = lines.map(({ group, line }) => ({ line, shares: sharesFor(group, line) }))
  const hasDistribution = covered.some((entry) => entry.shares.length > 0)
  if (!hasDistribution) {
    return { hasDistribution: false, complete: true, subtotalCents, lines: [] }
  }

  const complete =
    covered.every((entry) => entry.shares.length > 0) &&
    covered.every((entry) => Math.round(entry.shares.reduce((sum, s) => sum + sharePercent(s.percentage), 0) * 10000) === 1000000)
  if (!complete) {
    return { hasDistribution: true, complete: false, subtotalCents, lines: [] }
  }

  const totals = new Map<string, LiveShareLine>()
  for (const { line, shares } of covered) {
    const allocated = allocateShares(toCents(line.amount), shares.map((share) => sharePercent(share.percentage)))
    shares.forEach((share, index) => {
      let bucket = totals.get(share.beneficiary)
      if (!bucket) {
        bucket = { beneficiary: share.beneficiary, label: labelOf(share.beneficiary), cents: 0, parts: [] }
        totals.set(share.beneficiary, bucket)
      }
      bucket.cents += allocated[index]
      bucket.parts.push({ description: line.description, percentage: sharePercent(share.percentage), cents: allocated[index] })
    })
  }

  return {
    hasDistribution: true,
    complete: true,
    subtotalCents,
    lines: [...totals.values()].filter((entry) => entry.cents !== 0),
  }
}

/** Eenregelige samenvatting onder de naam van een ontvanger. */
export function partsSummary(parts: LivePart[]): string {
  if (parts.length === 0) return ''
  const percentages = parts.map((part) => part.percentage)
  const min = Math.min(...percentages)
  const max = Math.max(...percentages)
  const format = (value: number): string => `${value.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}%`

  if (parts.length === 1) return `${format(parts[0].percentage)} van "${parts[0].description}"`
  const count = `${parts.length} regels`
  return min === max ? `${count} · ${format(min)} van elke regel` : `${count} · ${format(min)} tot ${format(max)}`
}
